import {
  createCreditsPortalSessionAPI,
  fetchPlansAPI,
  purchasePlanAPI,
} from "../API/API";
import { alertNotification } from "../components/TopNotification/alertNotification";

export const BILLING_STORAGE_KEYS = {
  selectedPlan: "selectedPlan",
  selectedBillingCycle: "selectedBillingCycle",
  openUpgradeModalAfterSignup: "openUpgradeModalAfterSignup",
  checkoutReturnPath: "billingCheckoutReturnPath",
  checkoutSuccess: "billingCheckoutSuccess",
};

export const OPEN_UPGRADE_MODAL_EVENT = "open-upgrade-plan-modal";
export const DEFAULT_BILLING_RETURN_PATH = "/employerdashboard";
export const DEFAULT_BILLING_PORTAL_RETURN_PATH = "/myprofile";
export const BILLING_ELIGIBLE_SOURCE = "skillradar";

const BILLING_RETURN_BASE_PREFIXES = Array.from(
  new Set(
    [process.env.PUBLIC_URL, "/employer"]
      .filter(Boolean)
      .map((value) => `/${String(value).trim().replace(/^\/+|\/+$/g, "")}`)
      .filter((value) => value !== "/")
  )
);

export const normalizeSelectedPlan = (selectedPlan = "") => {
  if (typeof selectedPlan !== "string") {
    return "";
  }

  return selectedPlan
    .trim()
    .replace(/^['"]+|['"]+$/g, "")
    .toLowerCase();
};

export const getStoredUserProfile = () => {
  try {
    return JSON.parse(localStorage.getItem("userProfile") || "null");
  } catch {
    return null;
  }
};

export const isSkillRadarUser = (userProfile = getStoredUserProfile()) => {
  // Source is top-level on the /profile user object but nested under .profile in
  // the company-details response, and either one can be the last thing written
  // to the stored profile.
  const source = String(
    userProfile?.Source || userProfile?.profile?.Source || ""
  )
    .trim()
    .toLowerCase();
  return source === BILLING_ELIGIBLE_SOURCE;
};

export const canUseSubscriptionFeatures = (userProfile = getStoredUserProfile()) =>
  isSkillRadarUser(userProfile);

export const getCreditsBalancePayload = (creditsBalance = {}) =>
  creditsBalance?.data && typeof creditsBalance.data === "object"
    ? creditsBalance.data
    : creditsBalance || {};

export const isEnterprisePlan = (creditsBalance = {}) => {
  const balancePayload = getCreditsBalancePayload(creditsBalance);
  const normalizedPlanCode = normalizeSelectedPlan(
    balancePayload?.planCode || balancePayload?.productCode || ""
  );
  const planTokens = normalizedPlanCode.split(/[_\s-]+/);

  return planTokens.includes("enterprise");
};

export const canAccessProjectsPlan = (creditsBalance = {}) => {
  const balancePayload = getCreditsBalancePayload(creditsBalance);
  const normalizedPlanCode = normalizeSelectedPlan(
    balancePayload?.planCode || balancePayload?.productCode || ""
  );
  const planTokens = normalizedPlanCode.split(/[_\s-]+/);

  return planTokens.includes("enterprise");
};

export const getSelectedPlanFromPlanCode = (planCode = "") => {
  const normalizedPlanCode = normalizeSelectedPlan(planCode);

  if (!normalizedPlanCode) {
    return "starter";
  }

  return normalizedPlanCode
    .replace(/_(monthly|month|yearly|annual|annually)$/i, "")
    .trim() || "starter";
};

export const normalizeBillingCycle = (billingCycle = "") => {
  const normalizedValue =
    typeof billingCycle === "string"
      ? billingCycle.trim().replace(/^['"]+|['"]+$/g, "").toLowerCase()
      : "";

  if (normalizedValue === "annually" || normalizedValue === "annual") {
    return "yearly";
  }

  if (normalizedValue === "month" || normalizedValue === "monthly") {
    return "monthly";
  }

  return normalizedValue || "monthly";
};

export const persistSelectedPlanFromSearch = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  const selectedPlan = normalizeSelectedPlan(
    params.get(BILLING_STORAGE_KEYS.selectedPlan)
  );

  if (selectedPlan) {
    localStorage.setItem(BILLING_STORAGE_KEYS.selectedPlan, selectedPlan);
  }
};

export const getSelectedPlanFromSearch = (search = window.location.search) => {
  const params = new URLSearchParams(search);

  return normalizeSelectedPlan(params.get(BILLING_STORAGE_KEYS.selectedPlan));
};

export const getSelectedPlan = () => {
  return normalizeSelectedPlan(
    localStorage.getItem(BILLING_STORAGE_KEYS.selectedPlan)
  );
};

export const isDefaultFreePlanUser = (userProfile) => {
  if (!userProfile || typeof userProfile !== "object") {
    return true;
  }

  return userProfile?.IsPrivateEmployer !== true;
};

export const shouldShowUpgradePlanModal = ({
  search = window.location.search,
  userProfile,
} = {}) => {
  return (
    getSelectedPlanFromSearch(search) === "starter" &&
    isDefaultFreePlanUser(userProfile)
  );
};

export const getSelectedPlanLandingModalType = ({
  search = window.location.search,
  creditsBalanceResponse,
  creditsBalanceError,
} = {}) => {
  if (!getSelectedPlanFromSearch(search) || creditsBalanceError) {
    return "";
  }

  return shouldShowFreeTrialDashboardBanner(creditsBalanceResponse)
    ? "upgrade"
    : "manageSubscription";
};

export const setSelectedBillingCycle = (billingCycle) => {
  if (!billingCycle) {
    localStorage.removeItem(BILLING_STORAGE_KEYS.selectedBillingCycle);
    return;
  }

  localStorage.setItem(
    BILLING_STORAGE_KEYS.selectedBillingCycle,
    normalizeBillingCycle(billingCycle)
  );
};

export const getSelectedBillingCycle = () => {
  return normalizeBillingCycle(
    localStorage.getItem(BILLING_STORAGE_KEYS.selectedBillingCycle) || "monthly"
  );
};

export const getCurrentBillingReturnPath = () => {
  const { pathname, search, hash } = window.location;

  return `${pathname || ""}${search || ""}${hash || ""}` || DEFAULT_BILLING_RETURN_PATH;
};

export const sanitizeBillingReturnPath = (value = "") => {
  if (typeof value !== "string" || !value.trim()) {
    return DEFAULT_BILLING_RETURN_PATH;
  }

  let trimmedValue = value.trim();

  if (/^https?:\/\//i.test(trimmedValue)) {
    try {
      const parsedUrl = new URL(trimmedValue, window.location.origin);
      trimmedValue = `${parsedUrl.pathname || ""}${parsedUrl.search || ""}${parsedUrl.hash || ""}`;
    } catch (error) {
      return DEFAULT_BILLING_RETURN_PATH;
    }
  }

  if (!trimmedValue.startsWith("/") || trimmedValue.startsWith("//")) {
    return DEFAULT_BILLING_RETURN_PATH;
  }

  const [rawPathname = "/", rawSuffix = ""] = trimmedValue.split(/([?#].*)/, 2);
  let normalizedPathname = rawPathname || "/";
  let prefixRemoved = true;

  while (prefixRemoved) {
    prefixRemoved = false;

    for (const prefix of BILLING_RETURN_BASE_PREFIXES) {
      if (
        normalizedPathname === prefix ||
        normalizedPathname.startsWith(`${prefix}/`)
      ) {
        normalizedPathname =
          normalizedPathname.slice(prefix.length) || "/";
        prefixRemoved = true;
      }
    }
  }

  normalizedPathname = normalizedPathname.startsWith("/")
    ? normalizedPathname
    : `/${normalizedPathname}`;

  return `${normalizedPathname}${rawSuffix || ""}` || DEFAULT_BILLING_RETURN_PATH;
};

export const setBillingCheckoutReturnPath = (returnPath = getCurrentBillingReturnPath()) => {
  localStorage.setItem(
    BILLING_STORAGE_KEYS.checkoutReturnPath,
    sanitizeBillingReturnPath(returnPath)
  );
};

export const getBillingCheckoutReturnPath = () => {
  return sanitizeBillingReturnPath(
    localStorage.getItem(BILLING_STORAGE_KEYS.checkoutReturnPath) ||
      DEFAULT_BILLING_RETURN_PATH
  );
};

export const clearBillingCheckoutReturnPath = () => {
  localStorage.removeItem(BILLING_STORAGE_KEYS.checkoutReturnPath);
};

export const setBillingCheckoutSuccess = (payload = {}) => {
  localStorage.setItem(
    BILLING_STORAGE_KEYS.checkoutSuccess,
    JSON.stringify({
      title: "Payment successful",
      message:
        "Your plan purchase was completed successfully. You can continue from where you left off.",
      ...payload,
      createdAt: Date.now(),
    })
  );
};

export const consumeBillingCheckoutSuccess = () => {
  try {
    const storedValue = localStorage.getItem(BILLING_STORAGE_KEYS.checkoutSuccess);

    if (!storedValue) {
      return null;
    }

    localStorage.removeItem(BILLING_STORAGE_KEYS.checkoutSuccess);
    return JSON.parse(storedValue);
  } catch (error) {
    localStorage.removeItem(BILLING_STORAGE_KEYS.checkoutSuccess);
    return null;
  }
};

export const getPlanPurchaseRedirectUrls = ({
  returnPath = getCurrentBillingReturnPath(),
} = {}) => {
  const appBaseUrl = (
    process.env.REACT_APP_MY_URL ||
    `${window.location.origin}${process.env.PUBLIC_URL || ""}`
  ).replace(/\/+$/, "");
  const safeReturnPath = sanitizeBillingReturnPath(returnPath);
  const encodedReturnPath = encodeURIComponent(safeReturnPath);

  return {
    successUrl: `${appBaseUrl}/success?returnTo=${encodedReturnPath}`,
    cancelUrl: `${appBaseUrl}/canceled?returnTo=${encodedReturnPath}`,
  };
};

export const getBillingAbsoluteReturnUrl = (
  returnPath = DEFAULT_BILLING_PORTAL_RETURN_PATH
) => {
  const appBaseUrl = (
    process.env.REACT_APP_MY_URL ||
    `${window.location.origin}${process.env.PUBLIC_URL || ""}`
  ).replace(/\/+$/, "");
  const safeReturnPath = sanitizeBillingReturnPath(returnPath);

  return `${appBaseUrl}${safeReturnPath}`;
};

export const getPlanPurchaseProductCode = ({
  selectedPlan,
  billingCycle,
} = {}) => {
  const normalizedSelectedPlan = normalizeSelectedPlan(selectedPlan);
  const cycleKey = normalizeBillingCycle(billingCycle || getSelectedBillingCycle());

  if (normalizedSelectedPlan !== "starter") {
    return "";
  }

  if (cycleKey === "yearly") {
    return "STARTER_YEARLY";
  }

  return "STARTER_MONTHLY";
};

const TOP_UP_PRODUCT_TYPE = "topup";

const getTopUpPlanList = (responseData) => {
  const candidates = getCheckoutResponseCandidates(responseData);

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }

    if (Array.isArray(candidate?.plans)) {
      return candidate.plans;
    }

    if (Array.isArray(candidate?.data)) {
      return candidate.data;
    }

    if (Array.isArray(candidate?.payload)) {
      return candidate.payload;
    }
  }

  return [];
};

const getTopUpPlanCredits = (plan = {}) => {
  return (
    Number(plan?.credits) ||
    Number(plan?.creditAmount) ||
    Number(plan?.creditsAmount) ||
    Number(plan?.quantity) ||
    Number(plan?.units) ||
    0
  );
};

const getTopUpPlanPrice = (plan = {}) => {
  return (
    Number(plan?.priceUsd) ||
    Number(plan?.amountUsd) ||
    0
  );
};

const getTopUpPlanProductCode = (plan = {}) => {
  return (
    plan?.productCode ||
    plan?.planCode ||
    plan?.code ||
    plan?.sku ||
    ""
  );
};

const getTopUpPlanLabel = (plan = {}, credits = 0) => {
  if (plan?.label) {
    return plan.label;
  }

  if (plan?.displayName) {
    return plan.displayName;
  }

  if (plan?.name) {
    return plan.name;
  }

  if (credits >= 1000 && credits % 1000 === 0) {
    return `${credits / 1000}k`;
  }

  if (credits > 1000) {
    const roundedValue = Number((credits / 1000).toFixed(1));
    return `${roundedValue}k`;
  }

  return credits ? String(credits) : "Custom";
};

const normalizeTopUpPlan = (plan = {}) => {
  const credits = getTopUpPlanCredits(plan);
  const productCode = getTopUpPlanProductCode(plan);
  const isCustom =
    plan?.isCustom === true ||
    normalizePlanText(plan?.type) === "custom" ||
    normalizePlanText(plan?.productType) === "custom" ||
    (!credits && !productCode);

  return {
    ...plan,
    credits,
    label: getTopUpPlanLabel(plan, credits),
    price: getTopUpPlanPrice(plan),
    productCode,
    isCustom,
  };
};

export const fetchTopUpPlanOptions = async () => {
  const response = await fetchPlansAPI({ productType: TOP_UP_PRODUCT_TYPE });
  const plans = getTopUpPlanList(response?.data || response);

  return plans
    .map(normalizeTopUpPlan)
    .filter((plan) => plan.isCustom || (plan.credits > 0 && plan.productCode))
    .sort((firstPlan, secondPlan) => {
      if (firstPlan.isCustom && !secondPlan.isCustom) {
        return 1;
      }

      if (!firstPlan.isCustom && secondPlan.isCustom) {
        return -1;
      }

      return firstPlan.credits - secondPlan.credits;
    });
};

export const parseCheckoutResponseData = (value) => {
  if (!value || typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

export const getCheckoutResponseCandidates = (responseData) => {
  const parsedRoot = parseCheckoutResponseData(responseData);
  const candidates = [
    parsedRoot,
    parsedRoot?.data,
    parsedRoot?.data?.data,
    parsedRoot?.payload,
    parsedRoot?.payload?.data,
    parseCheckoutResponseData(parsedRoot?.data),
    parseCheckoutResponseData(parsedRoot?.payload),
  ].filter(Boolean);

  return candidates;
};

const CURRENT_PLAN_RESPONSE_KEYS = [
  "currentPlan",
  "plan",
  "subscription",
  "subscriptionPlan",
  "activePlan",
  "membership",
  "package",
];

const PLAN_IDENTITY_KEYS = [
  "currentPlan",
  "currentPlanCode",
  "currentProductCode",
  "productCode",
  "planCode",
  "planId",
  "planName",
  "displayName",
  "name",
  "planFamily",
  "tier",
  "billingInterval",
  "subscriptionId",
];

const PLAN_BOOLEAN_TRUE_KEYS = [
  "hasActivePlan",
  "isPaid",
  "paid",
  "isSubscribed",
  "hasActiveSubscription",
  "subscriptionActive",
  "activeSubscription",
];

const TRIAL_BOOLEAN_TRUE_KEYS = [
  "isFree",
  "isTrial",
  "isTrialPlan",
  "isFreeTrial",
  "freeTrial",
  "trialActive",
  "isTrialActive",
];

const NO_ACTIVE_PLAN_BOOLEAN_KEYS = ["hasActivePlan"];

const normalizePlanText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).trim().toLowerCase();
};

const isObjectCandidate = (value) => {
  return value && typeof value === "object" && !Array.isArray(value);
};

const getCurrentPlanCandidates = (responseData) => {
  const responseCandidates = getCheckoutResponseCandidates(responseData).filter(
    isObjectCandidate
  );
  const candidates = [];

  responseCandidates.forEach((candidate) => {
    candidates.push(candidate);

    CURRENT_PLAN_RESPONSE_KEYS.forEach((key) => {
      if (isObjectCandidate(candidate?.[key])) {
        candidates.push(candidate[key]);
      }
    });
  });

  return candidates;
};

const hasTrialPlanSignal = (candidate = {}) => {
  const hasTrialBoolean = TRIAL_BOOLEAN_TRUE_KEYS.some(
    (key) =>
      candidate?.[key] === true || normalizePlanText(candidate?.[key]) === "true"
  );

  if (hasTrialBoolean) {
    return true;
  }

  const planText = PLAN_IDENTITY_KEYS.concat([
    "planType",
    "subscriptionType",
    "status",
    "subscriptionStatus",
  ])
    .map((key) => normalizePlanText(candidate?.[key]))
    .filter(Boolean)
    .join(" ");

  return planText.includes("trial") || planText.includes("free");
};

const hasNoActivePlanSignal = (candidate = {}) => {
  return NO_ACTIVE_PLAN_BOOLEAN_KEYS.some(
    (key) =>
      candidate?.[key] === false ||
      normalizePlanText(candidate?.[key]) === "false"
  );
};

const hasPlanIdentity = (candidate = {}) => {
  return PLAN_IDENTITY_KEYS.some((key) => Boolean(candidate?.[key]));
};

const hasPaidCurrentPlanSignal = (candidate = {}) => {
  const hasPaidBoolean = PLAN_BOOLEAN_TRUE_KEYS.some(
    (key) =>
      candidate?.[key] === true || normalizePlanText(candidate?.[key]) === "true"
  );

  return (
    hasPaidBoolean ||
    (hasPlanIdentity(candidate) && !hasTrialPlanSignal(candidate))
  );
};

export const shouldShowFreeTrialDashboardBanner = (creditsBalanceResponse) => {
  const candidates = getCurrentPlanCandidates(creditsBalanceResponse);

  if (candidates.some(hasTrialPlanSignal)) {
    return true;
  }

  if (candidates.some(hasNoActivePlanSignal)) {
    return true;
  }

  if (candidates.some(hasPaidCurrentPlanSignal)) {
    return false;
  }

  return true;
};

export const getCheckoutRedirectUrl = (responseData) => {
  const candidates = getCheckoutResponseCandidates(responseData);

  for (const candidate of candidates) {
    const redirectUrl =
      candidate?.url ||
      candidate?.checkoutUrl ||
      candidate?.redirectUrl ||
      candidate?.portalUrl ||
      candidate?.sessionUrl;

    if (redirectUrl) {
      return redirectUrl;
    }
  }

  return "";
};

export const getCheckoutErrorMessage = (responseData) => {
  const candidates = getCheckoutResponseCandidates(responseData);

  for (const candidate of candidates) {
    const errorMessage =
      candidate?.errorMessage || candidate?.message || candidate?.error;

    if (errorMessage) {
      return errorMessage;
    }
  }

  return "";
};

export const hasCheckoutError = (responseData) => {
  const candidates = getCheckoutResponseCandidates(responseData);

  return candidates.some((candidate) => {
    const statusValue = candidate?.status;
    const statusCode = Number(candidate?.statusCode) || 0;

    return (
      statusValue === false ||
      statusValue === "false" ||
      statusCode >= 400
    );
  });
};

export const markUpgradeModalForSignupSuccess = () => {
  if (!getSelectedPlan()) {
    return;
  }

  localStorage.setItem(BILLING_STORAGE_KEYS.openUpgradeModalAfterSignup, "true");
};

export const shouldOpenUpgradeModalAfterSignup = () => {
  return (
    localStorage.getItem(BILLING_STORAGE_KEYS.openUpgradeModalAfterSignup) === "true"
  );
};

export const clearUpgradeModalAfterSignup = () => {
  localStorage.removeItem(BILLING_STORAGE_KEYS.openUpgradeModalAfterSignup);
};

export const openUpgradePlanModal = (selectedPlan = "starter") => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(OPEN_UPGRADE_MODAL_EVENT, { detail: { selectedPlan } })
  );
};

export const openManageSubscriptionModal = ({
  selectedPlan = "starter",
  allowCurrentPlanCheckout = false,
} = {}) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(OPEN_UPGRADE_MODAL_EVENT, {
      detail: {
        selectedPlan,
        modalType: "manageSubscription",
        allowCurrentPlanCheckout,
      },
    })
  );
};

const normalizeFeatureList = (features = []) => {
  if (!Array.isArray(features)) {
    return [];
  }

  return features
    .map((feature) => {
      if (typeof feature === "string") {
        return feature;
      }

      return feature?.label || feature?.name || feature?.title || "";
    })
    .filter(Boolean);
};

const getMonthlyPriceFromPlan = (plan = {}) => {
  return Number(plan?.variants?.monthly?.priceUsd) || 0;
};

const getAnnualPriceFromPlan = (plan = {}) => {
  const yearlyVariant = plan?.variants?.yearly || plan?.variants?.annual || {};
  const monthlyEquivalentPrice =
    Number(yearlyVariant?.monthlyEquivalentPriceUsd) ||
    Number(yearlyVariant?.monthlyEquivalentPrice) ||
    Number(yearlyVariant?.monthlyEquivalentAmount) ||
    0;

  if (monthlyEquivalentPrice > 0) {
    return monthlyEquivalentPrice;
  }

  const yearlyPrice =
    Number(yearlyVariant?.priceUsd) ||
    Number(yearlyVariant?.price) ||
    Number(yearlyVariant?.amountUsd) ||
    Number(yearlyVariant?.amount) ||
    0;
  const monthlyPrice = getMonthlyPriceFromPlan(plan);
  const discountPercent = Number(yearlyVariant?.discountPercent) || 0;

  if (
    yearlyPrice > 0 &&
    monthlyPrice > 0 &&
    discountPercent > 0 &&
    yearlyPrice === monthlyPrice
  ) {
    return Number((monthlyPrice * (1 - discountPercent / 100)).toFixed(2));
  }

  return yearlyPrice;
};

export const getPlanDetailsForSelectedPlan = (plansResponse, selectedPlan) => {
  const toPlanArray = (response) =>
    Array.isArray(response?.data?.plans)
      ? response.data.plans
      : Array.isArray(response?.plans)
        ? response.plans
        : [];

  const normalizedSelectedPlan = normalizeSelectedPlan(selectedPlan);
  const plans = toPlanArray(plansResponse);

  if (!normalizedSelectedPlan || plans.length === 0) {
    return null;
  }

  const matchedPlan = plans.find((plan) => {
    const normalizedPlanFamily = normalizeSelectedPlan(plan?.planFamily || "");

    return normalizedPlanFamily === normalizedSelectedPlan;
  });

  if (!matchedPlan) {
    return null;
  }

  const monthlyVariant = matchedPlan?.variants?.monthly || null;
  const yearlyVariant = matchedPlan?.variants?.yearly || null;
  const monthlyCredits =
    Number(monthlyVariant?.credits) || Number(matchedPlan?.credits) || 0;
  const yearlyCredits =
    Number(yearlyVariant?.credits) || monthlyCredits || Number(matchedPlan?.credits) || 0;
  const features = normalizeFeatureList(matchedPlan?.features) || [];

  const normalizedFeatures =
    features.length > 0
      ? features
      : [
          "Multiple User Seats",
          `${monthlyCredits} Monthly Credits`,
          "Full AI Candidate Matching",
          "Pipeline Analytics",
          "Custom Dashboards",
        ];

  return {
    name: matchedPlan?.displayName || matchedPlan?.planFamily || "Upgrade",
    monthlyPrice: getMonthlyPriceFromPlan(matchedPlan),
    annualPrice: getAnnualPriceFromPlan(matchedPlan),
    annualBadge:
      matchedPlan?.variants?.yearly?.discountPercent
        ? `${matchedPlan.variants.yearly.discountPercent}% off`
        : "",
    features: normalizedFeatures,
    monthlyPlanCode:
      monthlyVariant?.planCode || monthlyVariant?.productCode || "",
    yearlyPlanCode:
      yearlyVariant?.planCode || yearlyVariant?.productCode || "",
    monthlyPlanName: matchedPlan?.displayName || matchedPlan?.planFamily || "",
    yearlyPlanName: matchedPlan?.displayName || matchedPlan?.planFamily || "",
    monthlyCredits,
    yearlyCredits,
  };
};

export const redirectToStripePackageCheckout = async (options = {}) => {
  const selectedPlan = normalizeSelectedPlan(options.selectedPlan || getSelectedPlan());
  const billingCycle = normalizeBillingCycle(
    options.billingCycle || getSelectedBillingCycle()
  );
  try {
    const productCode =
      options.productCode ||
      getPlanPurchaseProductCode({
        selectedPlan,
        billingCycle,
      });

    if (!productCode) {
      alertNotification("error", "This plan is not configured for checkout yet.");
      return;
    }

    setSelectedBillingCycle(billingCycle);
    const returnPath = sanitizeBillingReturnPath(
      options.returnPath || getCurrentBillingReturnPath()
    );
    setBillingCheckoutReturnPath(returnPath);

    const { successUrl, cancelUrl } = getPlanPurchaseRedirectUrls({ returnPath });
    const response = await purchasePlanAPI({
      productCode,
      successUrl,
      cancelUrl,
    });

      const responsePayload = parseCheckoutResponseData(response?.data || response);
      const redirectUrl = getCheckoutRedirectUrl(responsePayload);
      const checkoutErrorMessage =
        getCheckoutErrorMessage(responsePayload) ||
        getCheckoutErrorMessage(response);

      if (hasCheckoutError(responsePayload) || hasCheckoutError(response)) {
        alertNotification(
          "error",
          checkoutErrorMessage || "Unable to start plan purchase right now."
        );
        return;
      }

      if (!redirectUrl) {
        alertNotification(
          "error",
          checkoutErrorMessage ||
            "Checkout session URL was not returned."
        );
        return;
      }

      window.location.assign(redirectUrl);
  } catch (error) {
    const apiErrorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message;

    alertNotification(
      "error",
      apiErrorMessage || "Unable to start plan purchase right now."
    );
  }
};

export const redirectToBillingPortal = async (options = {}) => {
  try {
    const returnPath = sanitizeBillingReturnPath(
      options.returnPath || DEFAULT_BILLING_PORTAL_RETURN_PATH
    );
    const response = await createCreditsPortalSessionAPI({
      returnUrl: getBillingAbsoluteReturnUrl(returnPath),
    });
    const responsePayload = parseCheckoutResponseData(response?.data || response);
    const redirectUrl = getCheckoutRedirectUrl(responsePayload);
    const portalErrorMessage =
      getCheckoutErrorMessage(responsePayload) || getCheckoutErrorMessage(response);

    if (hasCheckoutError(responsePayload) || hasCheckoutError(response)) {
      alertNotification(
        "error",
        portalErrorMessage || "Unable to open billing portal right now."
      );
      return;
    }

    if (!redirectUrl) {
      alertNotification(
        "error",
        portalErrorMessage || "Billing portal URL was not returned."
      );
      return;
    }

    window.location.assign(redirectUrl);
  } catch (error) {
    const apiErrorMessage =
      error?.response?.data?.errorMessage ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message;

    alertNotification(
      "error",
      apiErrorMessage || "Unable to open billing portal right now."
    );
  }
};
